import React, { useState, useMemo } from 'react';
import { Form, Row, Col, Table, Button, Spinner, Modal, Alert } from 'react-bootstrap';
import Select from 'react-select';
import InMemoryDocuments from './InMemoryDocuments';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatCurrency, parseDateToString, formatDateOnly, formatQuantity } from '../../utils/commonUtils';


import 'bootstrap-icons/font/bootstrap-icons.css';
import '../../styles/vendorConfirmations.css';

const VendorConfirmations = ({
    confirmations,
    isEditingConfirmation,
    selectedConfirmation,
    confirmationForm,
    setConfirmationForm,
    handleEditConfirmation,
    handleConfirmationSubmit,
    handleCancelConfirmation,
    handleTrackContainer,
    handleDeleteConfirmation,
    isTracking,
    availableLines,
    handleAddConfirmLine,
    documents,
    pendingDocuments,
    setPendingDocuments,
    setDocumentsToDelete,
    poId,
    uploadProgress,
    processingDocuments,
    onError,
    error,
    isPOCompleted,
    onDownloadDocument,
    onConvertTemporaryItem
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showExtractedDataModal, setShowExtractedDataModal] = useState(false);
    const [extractedData, setExtractedData] = useState(null);
    const [highlightedFields, setHighlightedFields] = useState(new Set());

    // Filter documents for the selected confirmation
    const getConfirmationDocuments = useMemo(() => {
        if (!selectedConfirmation || !documents || !documents.documents) {
            return [];
        }
        
        return documents.documents.filter(doc => doc.confirmation_id === selectedConfirmation._id);
    }, [selectedConfirmation, documents]);





    const handleExtractedData = (data) => {
        // No need to filter anymore since we're getting clean data from the backend
        if (Object.keys(data).length > 0) {
            setExtractedData(data);
            setShowExtractedDataModal(true);
        }
    };

    const applyExtractedData = () => {
        // Track which fields are being updated
        const fieldsToHighlight = new Set();
        
        // Update confirmation form with extracted data
        setConfirmationForm(prev => {
            const updates = {};

            if (extractedData.confirmation_date) {
                updates.confirmation_date = parseDateToString(extractedData.confirmation_date);
                fieldsToHighlight.add('confirmation_date');
            }
            if (extractedData.etd) {
                updates.etd = parseDateToString(extractedData.etd);
                fieldsToHighlight.add('etd');
            }
            if (extractedData.eta) {
                updates.eta = parseDateToString(extractedData.eta);
                fieldsToHighlight.add('eta');
            }
            if (extractedData.carrier_name) {
                updates.carrier_name = extractedData.carrier_name;
                fieldsToHighlight.add('carrier_name');
            }
            if (extractedData.container_number) {
                updates.container_number = extractedData.container_number;
                fieldsToHighlight.add('container_number');
            }

                            // Handle matched items if they exist
            if (extractedData.matched_items && extractedData.matched_items.length > 0) {
                // Create a map to store processed items by their identifying characteristics
                const processedItemsMap = new Map();
                
                console.log('Available PO Lines:', availableLines);
                
                // Process all matched items first
                extractedData.matched_items.forEach(matchedItem => {
                    console.log('Processing matched item:', matchedItem);
                    
                    // Get PO Line ID from the matched item
                    const poLineId = matchedItem.matched_item?.po_line_id;
                    console.log('Looking for PO Line ID:', poLineId);
                    
                    // Only look for exact PO Line ID match
                    let poLine = poLineId ? availableLines.find(line => line._id === poLineId) : null;
                    console.log('Found PO Line:', poLine);

                    let processedLine;
                    if (poLine) {
                        // Create line with data from PO line
                        processedLine = {
                            po_line_id: poLine._id,
                            stock_item: poLine.stock_item_description || poLine.description,
                            original_quantity: poLine.quantity,
                            confirmed_quantity: matchedItem.quantity || poLine.quantity,
                            notes: matchedItem.unit_price ? 
                                `Price: ${formatCurrency(matchedItem.unit_price)}${matchedItem.line_total ? `, Total: ${formatCurrency(matchedItem.line_total)}` : ''}` : 
                                '',
                            isConfirmed: true
                        };
                        console.log('Created line from PO:', processedLine);
                    } else {
                        // If no PO line match, check if this item already exists in the form
                        const existingLine = confirmationForm.confirmed_lines.find(line => 
                            line.po_line_id === matchedItem.matched_item?.po_line_id
                        );
                        console.log('Existing line found:', existingLine);

                        // Create new line, preserving original quantity if it exists
                        processedLine = {
                            po_line_id: matchedItem.matched_item?.po_line_id || `temp_${Date.now()}`,
                            stock_item: matchedItem.matched_item?.description,
                            original_quantity: existingLine ? existingLine.original_quantity : 0,
                            confirmed_quantity: matchedItem.quantity || 0,
                            notes: matchedItem.unit_price ? 
                                `Price: ${formatCurrency(matchedItem.unit_price)}${matchedItem.line_total ? `, Total: ${formatCurrency(matchedItem.line_total)}` : ''}` : 
                                '',
                            isConfirmed: true,
                            vendor_item_id: matchedItem.matched_item?.vendor_item_id,
                            gtin: matchedItem.matched_item?.gtin
                        };
                    }

                    if (processedLine) {
                        // Use stock_item as key for matching
                        const key = processedLine.stock_item.toLowerCase().trim();
                        processedItemsMap.set(key, processedLine);
                    }
                });

                // Now process existing lines, keeping only those that don't match with processed items
                const remainingLines = prev.confirmed_lines.filter(line => {
                    const key = line.stock_item.toLowerCase().trim();
                    return !processedItemsMap.has(key);
                });

                // Combine remaining lines with processed lines
                updates.confirmed_lines = [...remainingLines, ...processedItemsMap.values()];
            }
            
            return { ...prev, ...updates };
        });

        // Set highlighted fields
        setHighlightedFields(fieldsToHighlight);
        
        // Clear highlights after animation
        setTimeout(() => {
            setHighlightedFields(new Set());
        }, 2000); // Duration matches CSS animation

        setShowExtractedDataModal(false);
    };

    // Add function to handle line removal
    const handleRemoveLine = (idx) => {
        setConfirmationForm(prev => ({
            ...prev,
            confirmed_lines: prev.confirmed_lines.filter((_, index) => index !== idx)
        }));
    };

    // Add validation function for confirmation form
    const isConfirmationFormValid = useMemo(() => {
        try {
            // 1. Check if mandatory shipping details are set
            // Only confirmation_date is mandatory, carrier_name and container_number are optional
            const mandatoryFieldsValid = Boolean(
                confirmationForm.confirmation_date
            );

            // 2. Check if there is at least one item with confirmed quantity
            const hasConfirmedItems = Boolean(
                Array.isArray(confirmationForm.confirmed_lines) &&
                confirmationForm.confirmed_lines.length > 0 &&
                confirmationForm.confirmed_lines.some(line => {
                    if (!line) return false;
                    const quantity = Number(line.confirmed_quantity);
                    const hasValidQuantity = !isNaN(quantity) && quantity > 0;
                    return hasValidQuantity;
                })
            );

            return mandatoryFieldsValid && hasConfirmedItems;
        } catch (error) {
            console.error('Error in validation:', error);
            return false;
        }
    }, [
        confirmationForm.confirmation_date,
        confirmationForm.confirmed_lines
    ]);

    // Get validation messages for missing requirements
    const validationMessages = useMemo(() => {
        const messages = [];

        if (!confirmationForm.confirmation_date) {
            messages.push("Confirmation date is required");
        }

        // Carrier name and container number are now optional - no validation messages needed

        if (!confirmationForm.confirmed_lines.some(line => line.isConfirmed && Number(line.confirmed_quantity) > 0)) {
            messages.push("At least one confirmed line item with quantity is required");
        }

        return messages;
    }, [
        confirmationForm.confirmation_date,
        confirmationForm.confirmed_lines
    ]);

    return (
        <div className="mt-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Vendor Confirmations</h2>
                <div className="d-flex gap-2">
                    {!isEditingConfirmation && !isPOCompleted && (
                        <Button 
                            variant="success" 
                            onClick={() => handleEditConfirmation()}
                        >
                            Add Confirmation
                        </Button>
                    )}
                </div>
            </div>

            {isEditingConfirmation ? (
                <div className="card mb-4">
                    <div className="card-header bg-body-secondary">
                        <h4 className="mb-0">
                            {selectedConfirmation ? 'Edit Confirmation' : 'New Confirmation'}
                        </h4>
                    </div>
                    <div className="card-body">
                        <Form onSubmit={handleConfirmationSubmit}>
                            <div className="card mb-4 border-primary">
                                <div className="card-header bg-primary text-white">
                                    <h4 className="mb-0">Documents</h4>
                                </div>
                                <div className="card-body bg-body-tertiary">
                                    {/* Always use InMemoryDocuments for atomic document management */}
                                    <InMemoryDocuments
                                        pendingDocuments={pendingDocuments}
                                        setPendingDocuments={setPendingDocuments}
                                        onExtractedData={handleExtractedData}
                                        poId={poId}
                                        existingDocuments={selectedConfirmation ? getConfirmationDocuments : []}
                                        isEditMode={!!selectedConfirmation}
                                        onDocumentsToDelete={setDocumentsToDelete}
                                        documentUploadProgress={uploadProgress}
                                        isDocumentUploading={processingDocuments}
                                        disabled={isPOCompleted}
                                        onDownloadDocument={onDownloadDocument}
                                    />
                                </div>
                            </div>

                            <div className="card mb-4 border-primary">
                                <div className="card-header bg-primary text-white">
                                    <h4 className="mb-0">Shipping Details</h4>
                                </div>
                                <div className="card-body bg-body-tertiary">
                                    <div className="shipping-details-table">
                                        <div className="shipping-details-row">
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Confirmation Date*</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={confirmationForm.confirmation_date || ''}
                                                        onChange={e => setConfirmationForm(prev => ({
                                                            ...prev,
                                                            confirmation_date: e.target.value
                                                        }))}
                                                        className={highlightedFields.has('confirmation_date') ? 'highlight-update' : ''}
                                                        disabled={isPOCompleted}
                                                        autoComplete="off"
                                                        spellCheck="false"
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">ETD</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={confirmationForm.etd || ''}
                                                        onChange={e => setConfirmationForm(prev => ({
                                                            ...prev,
                                                            etd: e.target.value
                                                        }))}
                                                        className={highlightedFields.has('etd') ? 'highlight-update' : ''}
                                                        disabled={isPOCompleted}
                                                        autoComplete="off"
                                                        spellCheck="false"
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">ETA</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={confirmationForm.eta || ''}
                                                        onChange={e => setConfirmationForm(prev => ({
                                                            ...prev,
                                                            eta: e.target.value
                                                        }))}
                                                        className={highlightedFields.has('eta') ? 'highlight-update' : ''}
                                                        disabled={isPOCompleted}
                                                        autoComplete="off"
                                                        spellCheck="false"
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Carrier Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={confirmationForm.carrier_name}
                                                        onChange={e => setConfirmationForm(prev => ({
                                                            ...prev,
                                                            carrier_name: e.target.value
                                                        }))}
                                                        name="carrier_name"
                                                        className={highlightedFields.has('carrier_name') ? 'highlight-update' : ''}
                                                        disabled={isPOCompleted}
                                                        autoComplete="off"
                                                        spellCheck="false"
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Container Number</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            type="text"
                                                            value={confirmationForm.container_number}
                                                            onChange={e => setConfirmationForm(prev => ({
                                                                ...prev,
                                                                container_number: e.target.value.trim().toUpperCase()
                                                            }))}
                                                            name="container_number"
                                                            className={highlightedFields.has('container_number') ? 'highlight-update' : ''}
                                                            style={{ textTransform: 'uppercase' }}
                                                            disabled={isPOCompleted}
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                        <Button
                                                            variant="info"
                                                            disabled={isTracking || !confirmationForm.container_number || isPOCompleted}
                                                            onClick={() => handleTrackContainer(confirmationForm.container_number)}
                                                        >
                                                            {isTracking ? <Spinner animation="border" size="sm" /> : 'Track'}
                                                        </Button>
                                                    </div>
                                                </Form.Group>
                                            </div>
                                            <div className="shipping-details-cell">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">BOL Number</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={confirmationForm.bol_number || ''}
                                                        onChange={e => setConfirmationForm(prev => ({
                                                            ...prev,
                                                            bol_number: e.target.value.trim().toUpperCase()
                                                        }))}
                                                        name="bol_number"
                                                        className={highlightedFields.has('bol_number') ? 'highlight-update' : ''}
                                                        style={{ textTransform: 'uppercase' }}
                                                        disabled={isPOCompleted}
                                                        autoComplete="off"
                                                        spellCheck="false"
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            <div className="card mb-4 border-primary">
                                <div className="card-header bg-primary text-white">
                                    <h4 className="mb-0">Purchase Order Items</h4>
                                </div>
                                <div className="card-body bg-body-tertiary">
                                    {availableLines.length > 0 && (
                                        <div className="mb-3">
                                            <div className="d-flex gap-2 align-items-center">
                                                <Select
                                                    options={availableLines}
                                                    getOptionLabel={line => 
                                                        `${line.stock_item_description} (Ordered: ${line.quantity}, Remaining: ${line.remaining_quantity})`
                                                    }
                                                    getOptionValue={line => line._id}
                                                    onChange={selected => selected && handleAddConfirmLine(selected)}
                                                    placeholder="Select line to add..."
                                                    className="react-select-container flex-grow-1"
                                                    classNamePrefix="react-select"
                                                    isClearable={true}
                                                    value={null}
                                                    isDisabled={isPOCompleted}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <Table striped bordered>
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Original Qty</th>
                                                <th>Confirm Qty</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {confirmationForm.confirmed_lines.map((line, idx) => {
                                                const isReceived = line.received_date !== null;
                                                return (
                                                <tr key={line.po_line_id}>
                                                    <td>{line.stock_item}</td>
                                                    <td>{line.original_quantity}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            value={line.confirmed_quantity}
                                                            onChange={e => {
                                                                const newLines = [...confirmationForm.confirmed_lines];
                                                                newLines[idx] = {
                                                                    ...line,
                                                                    confirmed_quantity: Number(e.target.value) || 0
                                                                };
                                                                setConfirmationForm(prev => ({
                                                                    ...prev,
                                                                    confirmed_lines: newLines
                                                                }));
                                                            }}
                                                            min="0"
                                                            disabled={isPOCompleted}
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        {isReceived && line.received_date ? (
                                                            <span className="text-success">
                                                                Received on {formatDateOnly(line.received_date)}
                                                            </span>
                                                        ) : (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleRemoveLine(idx)}
                                                                disabled={isPOCompleted}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>

                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={confirmationForm.notes}
                                            onChange={e => setConfirmationForm(prev => ({
                                                ...prev,
                                                notes: e.target.value
                                            }))}
                                            placeholder="Add any additional notes"
                                            disabled={isPOCompleted}
                                            autoComplete="off"
                                            spellCheck="true"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="d-flex flex-column align-items-end gap-2">
                                {validationMessages.length > 0 && (
                                    <div className="text-danger small">
                                        <ul className="mb-2">
                                            {validationMessages.map((message, index) => (
                                                <li key={index}>{message}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {error && (
                                    <Alert variant="danger" onClose={() => onError && onError(null)} dismissible className="mb-3">
                                        {error}
                                    </Alert>
                                )}
                                
                                <div className="d-flex justify-content-end gap-2">
                                    <Button 
                                        variant="secondary" 
                                        onClick={handleCancelConfirmation}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        type="submit"
                                        disabled={!isConfirmationFormValid || isPOCompleted}
                                    >
                                        {selectedConfirmation ? 'Update' : 'Create'} Confirmation
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Confirmation Date</th>
                                <th>ETD</th>
                                <th>ETA</th>
                                <th>Carrier</th>
                                <th>Container #</th>
                                <th>Lines</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {confirmations.map(conf => (
                                <React.Fragment key={conf._id}>
                                    <tr>
                                        <td>{formatDateOnly(conf.confirmation_date)}</td>
                                        <td>{formatDateOnly(conf.etd)}</td>
                                        <td>{formatDateOnly(conf.eta)}</td>
                                        <td>{conf.carrier_name || '-'}</td>
                                        <td>{conf.container_number || '-'}</td>
                                        <td>{conf.confirmed_lines.length}</td>
                                        <td className="text-end">
                                            <div className="d-flex gap-2 justify-content-end">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleEditConfirmation(conf)}
                                                >
                                                    Details
                                                </Button>

                                                {/* Only show Receive All if there are unreceived items, no temporary items, and PO is not completed */}
                                                {conf.confirmed_lines.some(line => !line.received_date) && 
                                                 !conf.confirmed_lines.some(line => line.is_temporary_item) &&
                                                 !isPOCompleted && (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Get the first line to access PO data
                                                            const firstLine = conf.confirmed_lines.find(l => l.po_line);
                                                            if (firstLine) {
                                                                navigate('/inventory-receive-batch', {
                                                                    state: {
                                                                        confirmation: conf,
                                                                        warehouse: firstLine.po_line.purchase_order.ship_to,
                                                                        vendor: firstLine.po_line.purchase_order.vendor,
                                                                        fromVendorConfirmation: true,
                                                                        poId: firstLine.po_line.purchase_order._id || poId
                                                                    }
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        Receive All
                                                    </Button>
                                                )}
                                                {/* Only show Delete button if no items have been received and PO is not completed */}
                                                {!conf.confirmed_lines.some(line => line.received_date) && !isPOCompleted && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteConfirmation(conf._id)}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {/* Add confirmed lines details */}
                                    <tr>
                                        <td colSpan="7" className="p-0">
                                            <Table className="mb-0">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th style={{width: '50%'}}>Item</th>
                                                        <th style={{width: '20%'}}>Confirmed Qty</th>
                                                        <th style={{width: '30%'}} className="text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {conf.confirmed_lines
                                                        .sort((a, b) => a.stock_item.localeCompare(b.stock_item))
                                                        .map((line, index) => {
                                                        const isReceived = line.received_date !== null;
                                                        const isTemporary = line.po_line && line.po_line.is_temporary_item;
                                                        return (
                                                        <tr key={index}>
                                                            <td>
                                                                {line.stock_item}
                                                                {isTemporary && (
                                                                    <span className="badge bg-warning text-dark ms-2">
                                                                        <i className="fas fa-exclamation-triangle"></i> TEMP
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>{formatQuantity(line.confirmed_quantity)}</td>
                                                            <td className="text-end">
                                                                {isReceived && line.received_date ? (
                                                                    <span className="text-success">
                                                                        Received on {formatDateOnly(line.received_date)}
                                                                    </span>
                                                                ) : (
                                                                    <div className="d-flex gap-2 justify-content-end">
                                                                        {!isPOCompleted && !isTemporary && (
                                                                            <Button
                                                                                variant="success"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigate(`/inventory-receive/${line.stock_item_id}`, {
                                                                                        state: { 
                                                                                            returnFilters: location.search,
                                                                                            confirmedPoLineId: line._id,
                                                                                            fromVendorConfirmation: true,
                                                                                            poId: line.po_line.purchase_order._id || poId,
                                                                                            initialData: {
                                                                                                quantity: line.confirmed_quantity,
                                                                                                vendor: line.po_line.purchase_order.vendor._id,
                                                                                                warehouse: line.po_line.purchase_order.ship_to._id,
                                                                                                bb_date: line.bb_date,
                                                                                                lot: line.lot
                                                                                            }
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            >
                                                                                Receive
                                                                            </Button>
                                                                        )}
                                                                        {isTemporary && onConvertTemporaryItem && line.po_line?._id && (
                                                                            <Button
                                                                                variant="warning"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    // Use the po_line data from the confirmed line
                                                                                    if (line.po_line) {
                                                                                        onConvertTemporaryItem(line.po_line);
                                                                                    }
                                                                                }}
                                                                                title="Convert this temporary item to a Stock Item"
                                                                            >
                                                                                <i className="fas fa-exchange-alt me-1"></i>
                                                                                Convert
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )})}
                                                </tbody>
                                            </Table>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}



            {/* Add Modal for Extracted Data */}
            <Modal 
                show={showExtractedDataModal} 
                onHide={() => setShowExtractedDataModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Document Data Found</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>The following information was extracted from the document. Would you like to use it?</p>
                    
                    {/* General Information Table */}
                    <h5>General Information</h5>
                    <Table striped bordered className="mb-4">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {extractedData && Object.entries(extractedData)
                                .filter(([key]) => key !== 'matched_items' && extractedData[key] !== null && extractedData[key] !== undefined)
                                .map(([key, value]) => (
                                    <tr key={key}>
                                        <td>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                                        <td>{value}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>

                    {/* Matched Items Table */}
                    {extractedData?.matched_items && extractedData.matched_items.length > 0 && (
                        <>
                            <h5>Matched Stock Items</h5>
                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th>Confirmed Item</th>
                                        <th>Confirmed Quantity</th>
                                        <th>Confirmed Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extractedData.matched_items.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div>{item.matched_item?.description || '-'}</div>
                                                <small className="text-muted">
                                                    {item.matched_item?.vendor_item_id && `Vendor ID: ${item.matched_item.vendor_item_id}`}
                                                    {item.matched_item?.gtin && `, GTIN: ${item.matched_item.gtin}`}
                                                </small>
                                            </td>
                                            <td>{formatQuantity(item.quantity) || '-'}</td>
                                            <td>{item.unit_price ? formatCurrency(item.unit_price) : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExtractedDataModal(false)}>
                        Ignore
                    </Button>
                    <Button variant="primary" onClick={applyExtractedData}>
                        Use This Data
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VendorConfirmations; 